import React, { useMemo, useState } from "react";
import { CardComp, ButtonComp } from "../../../components";
import { ReactComponent as GreenNaira } from "../../../assets/svgs/green-naira.svg";
import { ReactComponent as Cash } from "../../../assets/svgs/cash.svg";
import { ReactComponent as RedNaira } from "../../../assets/svgs/red-naira.svg";
import CardInfo from "../../Admin/Home/components/CardInfo";
import TransactionRow from "./components/TransactionRow";
import PageWrapper from "../modules/PageWrapper";
import { useMemberDashboardQuery } from "../../../store/member/memberDashboard/memberDashboardSlice";
import { 
  useGetMemberContributionHistoryQuery,
  useGetMemberContributionSummaryQuery 
} from "../../../store/member/member-contribution";
import {
  useGetPendingMemberLoanRequestsQuery,
  useGetApprovedMemberLoanListQuery,
  useGetDisbursedMemberLoanListQuery,
  useGetOverdueRepaymentLoanListQuery,
  useGetDueRepaymentLoanListQuery,
  useGetUpcomingRepaymentLoanListQuery,
  useGetMemberLoanSummaryQuery,
} from "../../../store/member/member-loan";
import { useUser } from "../../../hooks";
import { Link } from "react-router-dom";
import { formatMoney } from "../../../utils";
import { useGetTodayBirthdaysQuery } from "../../../store/member/memberBirthdaySlice";
import BirthdayWishModal from "./components/BirthdayWishModal";
import FallbackImage from "../../../components/Common/ImageWrapper";
import ActivityFeed from "../../../components/shared/ActivityFeed";
import { useCompanyCapabilities } from "../../../hooks/useCompanyCapabilities";

export default function LegacyMemberHome() {
  const { data } = useMemberDashboardQuery();
  const [selectedBirthday, setSelectedBirthday] = useState(null);
  const [showWishModal, setShowWishModal] = useState(false);
  
  const { data: birthdayData, isLoading: isLoadingBirthdays } = useGetTodayBirthdaysQuery();
  const todayBirthdays = birthdayData?.data || [];
  
  const handleWishClick = (member) => {
    setSelectedBirthday({
      id: member.id,
      name: `${member.first_name} ${member.last_name}`,
    });
    setShowWishModal(true);
  };
  const memberDashboardData = data ? data.data : null;
  const user = useUser();
  const { investment, marketplace, realEstate, hasAnyInvestorFeature } = useCompanyCapabilities();

  // Fetch contribution summary (totals from backend - more accurate and consistent)
  const { data: contributionSummary, isLoading: isLoadingContributionSummary } = useGetMemberContributionSummaryQuery();
  
  // Fetch contribution history for recent transactions display
  const { data: contributionHistoryData, isLoading: isLoadingContributionHistory } = useGetMemberContributionHistoryQuery({
    page: 1,
    limit: 1000, // Get all contributions for recent transactions
    filter_type: "all",
  });

  // Fetch loan data by status
  const { data: pendingLoansData, isLoading: isLoadingPendingLoans } = useGetPendingMemberLoanRequestsQuery({
    page: 1,
    member_id: user?.member_id,
  }, { skip: !user?.member_id });

  const { data: approvedLoansData, isLoading: isLoadingApprovedLoans } = useGetApprovedMemberLoanListQuery({
    page: 1,
    member_id: user?.member_id,
  }, { skip: !user?.member_id });

  const { data: disbursedLoansData, isLoading: isLoadingDisbursedLoans } = useGetDisbursedMemberLoanListQuery({
    page: 1,
    member_id: user?.member_id,
  }, { skip: !user?.member_id });

  // Fetch repayment data
  const { data: overdueRepaymentsData, isLoading: isLoadingOverdueRepayments } = useGetOverdueRepaymentLoanListQuery({
    page: 1,
    member_id: user?.member_id,
  }, { skip: !user?.member_id });

  const { data: dueRepaymentsData, isLoading: isLoadingDueRepayments } = useGetDueRepaymentLoanListQuery({
    page: 1,
    member_id: user?.member_id,
  }, { skip: !user?.member_id });

  const { data: upcomingRepaymentsData, isLoading: isLoadingUpcomingRepayments } = useGetUpcomingRepaymentLoanListQuery({
    page: 1,
    member_id: user?.member_id,
  }, { skip: !user?.member_id });

  // Fetch loan summary for paid repayment data
  const { data: loanSummary, isLoading: isLoadingLoanSummary } = useGetMemberLoanSummaryQuery(undefined, { skip: !user?.member_id });

  // Loading helpers
  const isFinancialSummaryLoading = isLoadingContributionSummary;

  const isLoanStatusLoading =
    isLoadingPendingLoans || isLoadingApprovedLoans || isLoadingDisbursedLoans;

  const isRepaymentSummaryLoading =
    isLoadingOverdueRepayments ||
    isLoadingDueRepayments ||
    isLoadingUpcomingRepayments ||
    isLoadingLoanSummary;

  // Use summary API totals (backend-calculated, mobile-friendly)
  // Endpoint: /members/contributions/summary?guard=member
  // Returns: { total_contributed, total_savings, total_shares, total_contribution }
  const contributionTotals = useMemo(() => {
    if (contributionSummary) {
      return {
        savings: parseFloat(contributionSummary.total_savings || 0),
        shares: parseFloat(contributionSummary.total_shares || 0),
        total: parseFloat(
          contributionSummary.total_contribution ||
            contributionSummary.total_contributed ||
            0
        ),
      };
    }

    return {
      savings: 0,
      shares: 0,
      total: 0,
    };
  }, [contributionSummary]);

  // Calculate loan totals by status
  // "Total Loans" should show only disbursed loans (loan_taken), matching the loan summary API
  const loanTotals = useMemo(() => {
    // Use loan summary API for accurate total (only disbursed loans)
    // This matches the backend logic: sum of all disbursed loan amounts
    const loanTaken = loanSummary?.loan_taken || 0;
    
    // Also calculate by status for other displays if needed
    const pendingLoans = pendingLoansData?.data?.company_loan_requests || [];
    const approvedLoans = approvedLoansData?.data?.company_loan_requests || [];
    const disbursedLoans = disbursedLoansData?.data?.company_loan_requests || [];

    const pendingTotal = pendingLoans.reduce((sum, loan) => sum + parseFloat(loan.amount || 0), 0);
    const approvedTotal = approvedLoans.reduce((sum, loan) => sum + parseFloat(loan.amount || 0), 0);
    const disbursedTotal = disbursedLoans.reduce((sum, loan) => sum + parseFloat(loan.amount || 0), 0);
    
    // Fallback: if loan summary not available, calculate from disbursed loans list
    // But prefer loan summary as it's more accurate and includes company_id filtering
    const calculatedDisbursedTotal = loanTaken > 0 ? loanTaken : disbursedTotal;

    return {
      pending: pendingTotal,
      approved: approvedTotal,
      disbursed: calculatedDisbursedTotal,
      total: calculatedDisbursedTotal, // Total Loans = only disbursed loans (loan_taken)
    };
  }, [pendingLoansData, approvedLoansData, disbursedLoansData, loanSummary]);

  // Calculate repayment totals
  const repaymentTotals = useMemo(() => {
    const overdueRepayments = overdueRepaymentsData?.data?.company_loan_requests || [];
    const dueRepayments = dueRepaymentsData?.data?.company_loan_requests || [];
    const upcomingRepayments = upcomingRepaymentsData?.data?.company_loan_requests || [];

    // Combine all repayments to filter for paid ones
    const allRepayments = [...overdueRepayments, ...dueRepayments, ...upcomingRepayments];

    // Filter for pending repayments (not paid)
    const pendingRepayments = allRepayments.filter((repayment) => {
      const status = (repayment?.breakdown_status || repayment?.status || "").toUpperCase();
      return status !== "PAID" && status !== "COMPLETED";
    });

    // Filter for paid repayments
    const paidRepayments = allRepayments.filter((repayment) => {
      const status = (repayment?.breakdown_status || repayment?.status || "").toUpperCase();
      return status === "PAID" || status === "COMPLETED";
    });

    // Calculate overdue total (only unpaid overdue)
    const overdueTotal = overdueRepayments
      .filter((repayment) => {
        const status = (repayment?.breakdown_status || repayment?.status || "").toUpperCase();
        return status !== "PAID" && status !== "COMPLETED";
      })
      .reduce((sum, repayment) => {
        return sum + parseFloat(repayment.amount || repayment.total_amount || 0);
      }, 0);

    // Calculate pending total (due + upcoming, excluding paid)
    const pendingTotal = pendingRepayments.reduce((sum, repayment) => {
      return sum + parseFloat(repayment.amount || repayment.total_amount || 0);
    }, 0);

    // Calculate paid total from filtered paid repayments
    // Also check loan summary if available (more accurate)
    const paidTotalFromRepayments = paidRepayments.reduce((sum, repayment) => {
      return sum + parseFloat(repayment.amount || repayment.total_amount || 0);
    }, 0);

    // Use loan summary if available, otherwise use calculated total
    const paidTotal = loanSummary?.paid_repayment?.total || 
                     loanSummary?.paid_repayment?.amount || 
                     memberDashboardData?.repayment_history?.total_paid || 
                     paidTotalFromRepayments;

    return {
      overdue: overdueTotal,
      pending: pendingTotal,
      paid: paidTotal,
    };
  }, [overdueRepaymentsData, dueRepaymentsData, upcomingRepaymentsData, memberDashboardData, loanSummary]);

  // Skeleton card used during loading states
  const SummarySkeleton = () => (
    <CardComp cardClassName="border-0 shadow-sm h-100">
      <div className="p-4 flex flex-col gap-3 h-100">
        <div
          style={{
            height: "48px",
            width: "48px",
            borderRadius: "9999px",
            background: "#f1f1f1",
          }}
        />
        <div className="flex-1">
          <div
            style={{
              height: "12px",
              width: "40%",
              background: "#f1f1f1",
              borderRadius: "6px",
              marginBottom: "8px",
            }}
          />
          <div
            style={{
              height: "28px",
              width: "70%",
              background: "#e5e5e5",
              borderRadius: "8px",
            }}
          />
        </div>
      </div>
    </CardComp>
  );

  const renderSummarySkeletonRow = (count = 3) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, idx) => (
        <SummarySkeleton key={`summary-skeleton-${idx}`} />
      ))}
    </div>
  );

  // Combine recent transactions from multiple sources
  const recentTransactions = useMemo(() => {
    const transactions = [];

    // Add contributions from contribution history (most recent first)
    const contributions = (contributionHistoryData?.contributions || [])
      .map((contribution) => ({
        id: `contribution-${contribution.contributionId ?? contribution.id}`,
        type: "contribution",
        amount: contribution.amount,
        status: contribution.status || "SUCCESSFUL",
        // Keep both: payment_date is often date-only; created_at has real clock time
        payment_date: contribution.payment_date,
        created_at: contribution.created_at,
        contribution_category: contribution.contribution_category || contribution.category,
        category: contribution.category || contribution.contribution_category,
        payment_method: contribution.payment_method,
      }));

    // Add transactions from dashboard API if available
    const dashboardTransactions = (memberDashboardData?.recentTransactions || []).map((tx) => ({
      ...tx,
      id: tx.id || `dashboard-${tx.created_at || Date.now()}`,
    }));

    // Combine and sort by date (most recent first)
    const allTransactions = [...contributions, ...dashboardTransactions];
    return allTransactions
      .sort((a, b) => {
        const dateA = new Date(a.created_at || a.payment_date || a.date || 0);
        const dateB = new Date(b.created_at || b.payment_date || b.date || 0);
        return dateB - dateA;
      })
      .slice(0, 10); // Maximum 10 items
  }, [contributionHistoryData, memberDashboardData]);

  const SummaryCard = ({ icon, amount, title, iconBg, textColor = "#45464E" }) => (
    <CardComp cardClassName="border-0 shadow-sm hover:shadow-md transition-all duration-300 h-100">
      <div className="p-4 flex flex-col gap-3 h-100">
        <div
          className={`h-12 w-12 rounded-full ${iconBg} grid place-content-center`}
        >
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-muted text-xs uppercase tracking-wide mb-1">{title}</p>
          <p className="fw-bold mb-0 text-[1.5rem]" style={{ color: textColor }}>
            {formatMoney(amount || 0)}
          </p>
        </div>
      </div>
    </CardComp>
  );

  const startingBalance =
    memberDashboardData?.loanRepayment?.starting_balance ?? 0;

  return (
    <PageWrapper title={"Dashboard"}>
      {/* Financial Summary Section */}
      <div className="mb-5">
        <h5 className="fw-bold text-dark mb-4 text-lg">Financial Summary</h5>
        {isFinancialSummaryLoading
          ? renderSummarySkeletonRow(3)
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <SummaryCard
                icon={<Cash fill="var(--color-secondary)" />}
                amount={contributionTotals.savings}
                title="Savings"
                iconBg="bg-[var(--color-secondary-soft)]"
              />
              <SummaryCard
                icon={<Cash fill="var(--color-primary)" />}
                amount={contributionTotals.shares}
                title="Shares"
                iconBg="bg-[var(--color-primary-soft)]"
              />
              <SummaryCard
                icon={<GreenNaira />}
                amount={contributionTotals.total}
                title="Total Contribution"
                iconBg="bg-[#eaf3ef]"
              />
            </div>
          )}
      </div>

      {hasAnyInvestorFeature ? (
        <div className="mb-5">
          <h5 className="fw-bold text-dark mb-4 text-lg">Invest &amp; Marketplace</h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {investment ? (
              <CardComp cardClassName="border-0 shadow-sm rounded-4 h-100">
                <div className="p-4 flex flex-col gap-3 h-100">
                  <h6 className="fw-bold mb-1">Investments</h6>
                  <p className="text-blacklight small mb-0 flex-1">
                    Browse investment opportunities and track your participation.
                  </p>
                  <Link to="/invest/investments">
                    <ButtonComp btnText="Open investments" btnClassName="w-100 rounded-10" />
                  </Link>
                </div>
              </CardComp>
            ) : null}
            {marketplace ? (
              <CardComp cardClassName="border-0 shadow-sm rounded-4 h-100">
                <div className="p-4 flex flex-col gap-3 h-100">
                  <h6 className="fw-bold mb-1">Marketplace</h6>
                  <p className="text-blacklight small mb-0 flex-1">
                    Shop cooperative products and manage your orders.
                  </p>
                  <Link to="/invest/marketplace">
                    <ButtonComp btnText="Open marketplace" btnClassName="w-100 rounded-10" />
                  </Link>
                </div>
              </CardComp>
            ) : null}
            {realEstate ? (
              <CardComp cardClassName="border-0 shadow-sm rounded-4 h-100">
                <div className="p-4 flex flex-col gap-3 h-100">
                  <h6 className="fw-bold mb-1">Property requests</h6>
                  <p className="text-blacklight small mb-0 flex-1">
                    Submit and track real estate service requests.
                  </p>
                  <Link to="/invest/requests?filter=real_estate">
                    <ButtonComp btnText="View requests" btnClassName="w-100 rounded-10" />
                  </Link>
                </div>
              </CardComp>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Today's Birthdays Section */}
      {todayBirthdays.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-bold text-dark mb-0 text-lg">🎉 Today's Birthdays</h5>
            <Link to="/member/birthdays" className="text-primary1 small text-decoration-none fw-bold">View All →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {todayBirthdays.map((member, i) => (
              <CardComp key={i} cardClassName="border-0 shadow-sm rounded-4 hover:shadow-md transition-all">
                <div className="p-4 text-center">
                  <div className="mb-3 position-relative d-inline-block">
                    <FallbackImage
                      src={member.photo || "/Staff/avatar.svg"}
                      width="64px"
                      height="64px"
                      className="rounded-circle border"
                    />
                    <div className="position-absolute" style={{ bottom: "-5px", right: "-5px", fontSize: "20px" }}>🎂</div>
                  </div>
                  <h6 
                    className="fw-bold text-black1 mb-1" 
                    style={{ 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis' 
                    }}
                  >
                    {member.first_name} {member.last_name}
                  </h6>
                  <p className="text-blacklight small mb-3">
                    Membership No: {member.member_no || "N/A"}
                  </p>
                  <ButtonComp
                    btnText="Send Wish"
                    btnClassName="w-100 text-white py-2.5 rounded-10 border-0 shadow-sm"
                    ButtonInlineStyle={{ backgroundColor: "var(--color-primary)" }}
                    onClick={() => handleWishClick(member)}
                  />
                </div>
              </CardComp>
            ))}
          </div>
        </div>
      )}

      {/* Loans by Status Section */}
      <div className="mb-5">
        <h5 className="fw-bold text-dark mb-4 text-lg">Loans by Status</h5>
        {isLoanStatusLoading
          ? renderSummarySkeletonRow(3)
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <SummaryCard
                icon={<Cash fill="#FFA500" />}
                amount={loanTotals.pending}
                title="Pending Applications"
                iconBg="bg-[rgba(255,165,0,0.1)]"
                textColor="#FFA500"
              />
              <SummaryCard
                icon={<Cash fill="#19BC31" />}
                amount={loanTotals.approved}
                title="Approved Loans"
                iconBg="bg-[rgba(25,188,49,0.1)]"
                textColor="#19BC31"
              />
              <SummaryCard
                icon={<Cash fill="var(--color-secondary)" />}
                amount={loanTotals.disbursed}
                title="Active Loans"
                iconBg="bg-[var(--color-secondary-soft)]"
                textColor="var(--color-secondary)"
              />
            </div>
          )}
      </div>

      {/* Repayment Status Section */}
      <div className="mb-5">
        <h5 className="fw-bold text-dark mb-4 text-lg">Repayment Status</h5>
        {isRepaymentSummaryLoading
          ? renderSummarySkeletonRow(3)
          : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SummaryCard
                icon={<RedNaira />}
                amount={repaymentTotals.overdue}
                title="Overdue Repayments"
                iconBg="bg-[rgba(255,67,67,0.1)]"
                textColor="#FF4343"
              />
              <SummaryCard
                icon={<Cash fill="#FFA500" />}
                amount={repaymentTotals.pending}
                title="Pending Repayments"
                iconBg="bg-[rgba(255,165,0,0.1)]"
                textColor="#FFA500"
              />
              <SummaryCard
                icon={<Cash fill="#19BC31" />}
                amount={repaymentTotals.paid}
                title="Paid Repayments"
                iconBg="bg-[rgba(25,188,49,0.1)]"
                textColor="#19BC31"
              />
            </div>
          )}
      </div>

      {/* Loan Repayment Card */}
      {startingBalance > 0 && (
        <div className="bg-white rounded-md p-4 mb-7">
          <div className="flex gap-3 items-center mb-2 lg:mb-0">
            <p className="lg:text-[21px] font-[600]">Loan Repayment</p>
            <p className="bg-[#FF43431A] text-[#FF4343] font-[600] text-sm rounded-full p-2">
              {formatMoney(startingBalance)}
            </p>
          </div>
            <div className="flex flex-wrap lg:flex-nowrap lg:items-center items-start gap-3 lg:gap-0 justify-between">
              <div className="flex items-center gap-3">
                <p>Due date:</p>
                <div className="flex items-center gap-1">
                  <div className="bg-[#318C641A] p-[5px] rounded-full">
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.5433 4.33337C5.26996 4.33337 5.0433 4.10671 5.0433 3.83337V1.83337C5.0433 1.56004 5.26996 1.33337 5.5433 1.33337C5.81663 1.33337 6.0433 1.56004 6.0433 1.83337V3.83337C6.0433 4.10671 5.81663 4.33337 5.5433 4.33337Z"
                        fill="#292D32"
                      />
                      <path
                        d="M10.8766 4.33337C10.6033 4.33337 10.3766 4.10671 10.3766 3.83337V1.83337C10.3766 1.56004 10.6033 1.33337 10.8766 1.33337C11.15 1.33337 11.3766 1.56004 11.3766 1.83337V3.83337C11.3766 4.10671 11.15 4.33337 10.8766 4.33337Z"
                        fill="#292D32"
                      />
                      <path
                        d="M5.87663 10.1667C5.78996 10.1667 5.70329 10.1467 5.62329 10.1133C5.53663 10.08 5.46996 10.0333 5.40329 9.97333C5.28329 9.84667 5.20996 9.68 5.20996 9.5C5.20996 9.41334 5.22996 9.32667 5.2633 9.24667C5.29663 9.16667 5.34329 9.09334 5.40329 9.02668C5.46996 8.96668 5.53663 8.92 5.62329 8.88666C5.86329 8.78666 6.16329 8.84001 6.34996 9.02668C6.46996 9.15334 6.54329 9.32667 6.54329 9.5C6.54329 9.54 6.53663 9.58668 6.52996 9.63335C6.52329 9.67335 6.50996 9.71334 6.48996 9.75334C6.47663 9.79334 6.45663 9.83334 6.42996 9.87334C6.40996 9.90667 6.37663 9.94 6.34996 9.97333C6.22329 10.0933 6.04996 10.1667 5.87663 10.1667Z"
                        fill="#292D32"
                      />
                      <path
                        d="M8.20996 10.1667C8.1233 10.1667 8.03663 10.1467 7.95663 10.1134C7.86996 10.08 7.8033 10.0334 7.73663 9.97337C7.61663 9.8467 7.5433 9.68004 7.5433 9.50004C7.5433 9.41337 7.5633 9.3267 7.59663 9.2467C7.62996 9.1667 7.67663 9.09338 7.73663 9.02671C7.8033 8.96671 7.86996 8.92003 7.95663 8.8867C8.19663 8.78003 8.49663 8.84004 8.6833 9.02671C8.8033 9.15338 8.87663 9.3267 8.87663 9.50004C8.87663 9.54004 8.86996 9.58671 8.8633 9.63338C8.85663 9.67338 8.8433 9.71337 8.8233 9.75337C8.80996 9.79337 8.78996 9.83337 8.7633 9.87337C8.7433 9.9067 8.70996 9.94003 8.6833 9.97337C8.55663 10.0934 8.3833 10.1667 8.20996 10.1667Z"
                        fill="#292D32"
                      />
                      <path
                        d="M10.5433 10.1667C10.4566 10.1667 10.37 10.1467 10.29 10.1134C10.2033 10.08 10.1366 10.0334 10.07 9.97337C10.0433 9.94003 10.0166 9.9067 9.98996 9.87337C9.96329 9.83337 9.94329 9.79337 9.92996 9.75337C9.90996 9.71337 9.89663 9.67338 9.88996 9.63338C9.88329 9.58671 9.87663 9.54004 9.87663 9.50004C9.87663 9.3267 9.94996 9.15338 10.07 9.02671C10.1366 8.96671 10.2033 8.92003 10.29 8.8867C10.5366 8.78003 10.83 8.84004 11.0166 9.02671C11.1366 9.15338 11.21 9.3267 11.21 9.50004C11.21 9.54004 11.2033 9.58671 11.1966 9.63338C11.19 9.67338 11.1766 9.71337 11.1566 9.75337C11.1433 9.79337 11.1233 9.83337 11.0966 9.87337C11.0766 9.9067 11.0433 9.94003 11.0166 9.97337C10.89 10.0934 10.7166 10.1667 10.5433 10.1667Z"
                        fill="#292D32"
                      />
                      <path
                        d="M5.87663 12.5C5.78996 12.5 5.70329 12.4801 5.62329 12.4467C5.54329 12.4134 5.46996 12.3667 5.40329 12.3067C5.28329 12.18 5.20996 12.0067 5.20996 11.8334C5.20996 11.7467 5.22996 11.66 5.2633 11.58C5.29663 11.4934 5.34329 11.42 5.40329 11.36C5.64996 11.1134 6.10329 11.1134 6.34996 11.36C6.46996 11.4867 6.54329 11.66 6.54329 11.8334C6.54329 12.0067 6.46996 12.18 6.34996 12.3067C6.22329 12.4267 6.04996 12.5 5.87663 12.5Z"
                        fill="#292D32"
                      />
                      <path
                        d="M8.20996 12.5C8.03663 12.5 7.8633 12.4267 7.73663 12.3067C7.61663 12.18 7.5433 12.0067 7.5433 11.8334C7.5433 11.7467 7.5633 11.66 7.59663 11.58C7.62996 11.4934 7.67663 11.42 7.73663 11.36C7.9833 11.1134 8.43663 11.1134 8.6833 11.36C8.7433 11.42 8.78996 11.4934 8.8233 11.58C8.85663 11.66 8.87663 11.7467 8.87663 11.8334C8.87663 12.0067 8.8033 12.18 8.6833 12.3067C8.55663 12.4267 8.3833 12.5 8.20996 12.5Z"
                        fill="#292D32"
                      />
                      <path
                        d="M10.5433 12.5001C10.37 12.5001 10.1966 12.4267 10.07 12.3067C10.01 12.2467 9.96329 12.1734 9.92996 12.0867C9.89663 12.0067 9.87663 11.9201 9.87663 11.8334C9.87663 11.7467 9.89663 11.6601 9.92996 11.5801C9.96329 11.4934 10.01 11.4201 10.07 11.3601C10.2233 11.2067 10.4566 11.1334 10.67 11.1801C10.7166 11.1867 10.7566 11.2001 10.7966 11.2201C10.8366 11.2334 10.8766 11.2534 10.9166 11.2801C10.95 11.3001 10.9833 11.3334 11.0166 11.3601C11.1366 11.4867 11.21 11.6601 11.21 11.8334C11.21 12.0067 11.1366 12.1801 11.0166 12.3067C10.89 12.4267 10.7166 12.5001 10.5433 12.5001Z"
                        fill="#292D32"
                      />
                      <path
                        d="M13.8766 7.05994H2.5433C2.26996 7.05994 2.0433 6.83327 2.0433 6.55994C2.0433 6.2866 2.26996 6.05994 2.5433 6.05994H13.8766C14.15 6.05994 14.3766 6.2866 14.3766 6.55994C14.3766 6.83327 14.15 7.05994 13.8766 7.05994Z"
                        fill="#292D32"
                      />
                      <path
                        d="M10.8766 15.6667H5.54329C3.10996 15.6667 1.70996 14.2667 1.70996 11.8334V6.16671C1.70996 3.73337 3.10996 2.33337 5.54329 2.33337H10.8766C13.31 2.33337 14.71 3.73337 14.71 6.16671V11.8334C14.71 14.2667 13.31 15.6667 10.8766 15.6667ZM5.54329 3.33337C3.63663 3.33337 2.70996 4.26004 2.70996 6.16671V11.8334C2.70996 13.74 3.63663 14.6667 5.54329 14.6667H10.8766C12.7833 14.6667 13.71 13.74 13.71 11.8334V6.16671C13.71 4.26004 12.7833 3.33337 10.8766 3.33337H5.54329Z"
                        fill="#292D32"
                      />
                    </svg>
                  </div>
                  <p>
                    {new Date(
                    memberDashboardData?.loanRepayment?.due_at
                    ).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div>
              <Link to="/member/loan_management/loan_repayment">
                <p className="font-[600] text-base bg-[var(--color-primary)] py-[10px] px-5 rounded-[10px] text-white">
                  Pay now
                </p>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Loan Application CTA */}
      <div
        className="flex flex-col md:flex-row justify-between items-center rounded-md p-10 text-white mb-7"
        style={{ background: "var(--color-primary-dark)" }}
        data-tour="tour-loan-cta"
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="absolute -top-20 left-8">
              <svg
                width="142"
                height="141"
                viewBox="0 0 142 141"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="70.9411"
                  cy="71.4894"
                  r="70.6726"
                  fill="white"
                  fillOpacity="0.1"
                />
              </svg>
            </div>
            <div className="p-4 rounded-full" style={{ background: "var(--color-primary-soft)" }}>
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.1002 18.22H6.31015C5.79015 18.22 5.30014 18.17 4.86014 18.07C4.63014 18.04 4.35015 17.96 4.06015 17.85C2.73015 17.35 1.16016 16.12 1.16016 13.06V7.90997C1.16016 4.63997 3.04015 2.76001 6.31015 2.76001H15.1002C17.8602 2.76001 19.6401 4.07998 20.1201 6.47998C20.2101 6.91998 20.2502 7.38997 20.2502 7.90997V13.06C20.2502 16.35 18.3802 18.22 15.1002 18.22ZM6.32013 4.28003C3.86013 4.28003 2.67014 5.46999 2.67014 7.92999V13.08C2.67014 14.87 3.30015 15.97 4.59015 16.46C4.79015 16.53 4.98016 16.58 5.16016 16.61C5.54016 16.69 5.91013 16.73 6.32013 16.73H15.1101C17.5701 16.73 18.7601 15.54 18.7601 13.08V7.92999C18.7601 7.50999 18.7302 7.13999 18.6602 6.79999C18.3202 5.09999 17.1601 4.28003 15.1101 4.28003H6.32013Z"
                  fill="#383838"
                />
                <path
                  d="M18.1001 21.22H9.31005C8.46005 21.22 7.70004 21.1 7.05004 20.85C5.58004 20.3 4.62003 19.14 4.29003 17.48C4.24003 17.23 4.32005 16.97 4.50005 16.8C4.68005 16.62 4.94006 16.55 5.19006 16.61C5.52006 16.68 5.89005 16.72 6.31005 16.72H15.1001C17.5601 16.72 18.7501 15.53 18.7501 13.07V7.91997C18.7501 7.49997 18.72 7.12996 18.65 6.78996C18.6 6.53996 18.6801 6.28997 18.8501 6.10997C19.0301 5.92997 19.2801 5.84996 19.5301 5.90996C21.9301 6.39996 23.2501 8.17997 23.2501 10.92V16.07C23.2501 19.35 21.3801 21.22 18.1001 21.22ZM6.13006 18.22C6.45006 18.8 6.93005 19.21 7.59005 19.45C8.07005 19.63 8.65003 19.72 9.32003 19.72H18.11C20.57 19.72 21.76 18.53 21.76 16.07V10.92C21.76 9.33997 21.27 8.28997 20.26 7.73997C20.26 7.79997 20.26 7.85997 20.26 7.91997V13.07C20.26 16.34 18.38 18.22 15.11 18.22H6.32003C6.25003 18.22 6.19006 18.22 6.13006 18.22Z"
                  fill="#383838"
                />
                <path
                  d="M10.7098 13.89C8.83984 13.89 7.31982 12.37 7.31982 10.5C7.31982 8.63 8.83984 7.10999 10.7098 7.10999C12.5798 7.10999 14.0999 8.63 14.0999 10.5C14.0999 12.37 12.5798 13.89 10.7098 13.89ZM10.7098 8.60999C9.66984 8.60999 8.81982 9.46 8.81982 10.5C8.81982 11.54 9.66984 12.39 10.7098 12.39C11.7498 12.39 12.5999 11.54 12.5999 10.5C12.5999 9.46 11.7498 8.60999 10.7098 8.60999Z"
                  fill="#292D32"
                />
                <path
                  d="M4.99023 13.4501C4.58023 13.4501 4.24023 13.1101 4.24023 12.7001V8.30005C4.24023 7.89005 4.58023 7.55005 4.99023 7.55005C5.40023 7.55005 5.74023 7.89005 5.74023 8.30005V12.7001C5.74023 13.1101 5.41023 13.4501 4.99023 13.4501Z"
                  fill="#292D32"
                />
                <path
                  d="M16.4199 13.4501C16.0099 13.4501 15.6699 13.1101 15.6699 12.7001V8.30005C15.6699 7.89005 16.0099 7.55005 16.4199 7.55005C16.8299 7.55005 17.1699 7.89005 17.1699 8.30005V12.7001C17.1699 13.1101 16.8399 13.4501 16.4199 13.4501Z"
                  fill="#292D32"
                />
              </svg>
            </div>
            <div className="absolute -top-5 -left-1">
              <svg
                width="142"
                height="141"
                viewBox="0 0 142 141"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="70.9411"
                  cy="71.4894"
                  r="70.6726"
                  fill="white"
                  fillOpacity="0.1"
                />
              </svg>
            </div>
          </div>
          <p className="text-sm md:text-lg lg:text-2xl font-[700]">
            Get suitable and affordable loan within 5 minutes
          </p>
        </div>
        <Link to="/member/loan_management/application">
          <p className="font-[600] bg-[var(--color-primary)] text-sm lg:text-base py-[10px] px-7 lg:px-12 rounded-xl">
            Apply now
          </p>
        </Link>
      </div>

      {/* Platform activity (investments / marketplace / KYC / notifications) */}
      <div className="mt-10 mb-5">
        <h3 className="fw-bold text-black1 mb-4">Recent activity</h3>
        <CardComp cardClassName="border-0 shadow-sm">
          <div className="p-3">
            <ActivityFeed
              guard="member"
              perPage={8}
              showFilters
              showPagination={false}
              groupByDate
              compact
              emptyMessage="No platform activity yet."
            />
          </div>
        </CardComp>
      </div>

      {/* Recent Transactions */}
      <div className="mt-10">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold text-black1 mb-0">Recent Transactions</h3>
          {recentTransactions.length > 0 && (
            <Link
              to="/member/transaction_history"
              className="text-primary1 text-decoration-none fw-semibold"
              style={{ fontSize: "14px" }}
            >
              View All →
            </Link>
          )}
        </div>
        <CardComp cardClassName={"border-0 shadow-sm"}>
          <div
            className="hide-scrollbar"
            style={{
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            {recentTransactions.length > 0 ? (
              recentTransactions.map((transaction, index) => (
                <TransactionRow
                  key={transaction?.id || index}
                  transaction={transaction}
                  isLast={index === recentTransactions.length - 1}
                />
              ))
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No recent transactions available.</p>
                <Link
                  to="/member/contribution/record"
                  className="text-primary1 text-decoration-none mt-2 d-inline-block"
                >
                  Record your first contribution →
                </Link>
              </div>
            )}
          </div>
        </CardComp>
      </div>

      <BirthdayWishModal
        show={showWishModal}
        onHide={() => setShowWishModal(false)}
        recipient={selectedBirthday}
      />
    </PageWrapper>
  );
}
